import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMachine } from '@xstate/react';
import { ChoiceArena } from './components/ChoiceArena';
import { CommonsPanel } from './components/CommonsPanel';
import { ConsentGate, CONSENT_TEXT, CONSENT_VERSION } from './components/ConsentGate';
import { CurveChart } from './components/CurveChart';
import { FingerprintCard } from './components/FingerprintCard';
import { TurnstileBox } from './components/TurnstileBox';
import { loadCommons, loadConfig, submitFingerprint } from './lib/api';
import { clearUnsent, getOrCreateDeviceKey, hasUnsent, rememberUnsent } from './lib/device';
import type { CommonsStats, ConfigResponse } from './lib/types';
import { playMachine } from './machines/playMachine';
import { selectFingerprint, useGameStore } from './store/useGameStore';

function useCommons() {
  const [stats, setStats] = useState<CommonsStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    try {
      setStats(await loadCommons());
      setError(null);
    } catch {
      setError('Commons are offline. Your local fingerprint still works.');
    }
  }, []);
  useEffect(() => {
    refresh();
  }, [refresh]);
  return { stats, setStats, error, refresh };
}

export default function App() {
  const [state, send] = useMachine(playMachine);
  const game = useGameStore();
  const fingerprint = useGameStore(selectFingerprint);
  const currentTrial = game.trials[game.currentIndex];
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [saveNote, setSaveNote] = useState<string | null>(null);
  const { stats, setStats, error: commonsError } = useCommons();
  const unsent = hasUnsent();

  useEffect(() => {
    loadConfig()
      .then(setConfig)
      .catch(() =>
        setConfig({ turnstileSiteKey: '1x00000000000000000000AA', turnstileMode: 'test' })
      );
  }, []);

  useEffect(() => {
    if (game.isComplete() && state.matches('playing')) send({ type: 'COMPLETE' });
  }, [game, state, send]);

  const onStart = () => {
    game.reset();
    game.begin();
    setSaveNote(null);
    send({ type: 'START' });
  };

  const onAnswer = (choice: 'sooner' | 'later') => {
    game.answer(choice);
  };

  const onSubmit = async () => {
    if (!turnstileToken) {
      setSaveNote('Turnstile needs to verify this browser before saving to the commons.');
      return;
    }
    send({ type: 'SUBMIT' });
    const payload = {
      deviceKey: getOrCreateDeviceKey(),
      turnstileToken,
      consentText: CONSENT_TEXT,
      consentVersion: CONSENT_VERSION,
      startedAt: game.startedAtIso,
      answers: game.answers,
      fingerprint
    };
    try {
      const response = await submitFingerprint(payload);
      clearUnsent();
      setStats(response.stats);
      setSaveNote('Saved. Your curve is now one anonymous thread in the commons map.');
      send({ type: 'SUBMIT_OK' });
    } catch (error) {
      rememberUnsent(payload);
      const message = error instanceof Error ? error.message : 'Submission failed';
      setSaveNote(`${message}. I kept a local unsent copy in this browser.`);
      send({ type: 'SUBMIT_ERR', error: message });
    }
  };

  const stageLabel = useMemo(() => {
    if (state.matches('consent')) return 'consent';
    if (state.matches('ready')) return 'ready';
    if (state.matches('playing')) return 'playing';
    if (state.matches('revealing')) return 'revealing';
    if (state.matches('submitting')) return 'saving';
    return 'archived';
  }, [state]);

  return (
    <main className="app-shell">
      <header className="hero">
        <nav className="top-nav" aria-label="Primary">
          <a href="#play">play</a>
          <a href="#fingerprint">fingerprint</a>
          <a href="#commons">commons</a>
          <a href="#method">method</a>
          <a className="creator-link" href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer">LinkedIn</a>
          <a className="creator-link coffee" href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer">Buy Me a Coffee</a>
        </nav>
        <div className="hero-grid">
          <div>
            <p className="eyebrow">hyperbolic discounting · d1 commons · no accounts</p>
            <h1>Patience Fingerprint</h1>
            <p className="hero-copy">
              A little intertemporal-choice arcade that sketches how sharply your future fades — then gives the map back to you.
            </p>
            <div className="hero-actions">
              {state.matches('consent') ? (
                <button className="primary-action" onClick={() => send({ type: 'CONSENT' })}>Begin</button>
              ) : (
                <button className="primary-action" onClick={onStart}>Play a fresh curve</button>
              )}
              <span className="status-chip">stage: {stageLabel}</span>
              {config?.turnstileMode === 'test' ? <span className="status-chip warn">Turnstile test key</span> : null}
            </div>
          </div>
          <div className="hero-orb" aria-hidden="true">
            <div className="orb-core">k</div>
            <span className="orbit one" />
            <span className="orbit two" />
            <span className="orbit three" />
          </div>
        </div>
      </header>

      <section id="play" className="layout-grid">
        <div>
          {state.matches('consent') ? <ConsentGate onConsent={() => send({ type: 'CONSENT' })} /> : null}
          {state.matches('ready') ? (
            <section className="panel ready-panel">
              <p className="eyebrow">ready</p>
              <h2>The future is about to get priced.</h2>
              <p className="arena-copy">
                You’ll answer 18 fast choices. Each answer tightens a bracket around the present value of a later reward.
              </p>
              <button className="primary-action" onClick={onStart}>Start 18 rounds</button>
            </section>
          ) : null}
          {state.matches('playing') && currentTrial ? (
            <ChoiceArena trial={currentTrial} progress={game.currentIndex} total={game.trials.length} onAnswer={onAnswer} />
          ) : null}
          {(state.matches('revealing') || state.matches('submitting') || state.matches('archived')) ? (
            <div id="fingerprint">
              <FingerprintCard fingerprint={fingerprint} />
              <section className="panel save-panel">
                <p className="eyebrow">save to commons</p>
                <h2>Human check, then the map grows.</h2>
                <p className="consent-line"><span>{CONSENT_TEXT}</span></p>
                {config ? <TurnstileBox siteKey={config.turnstileSiteKey} onToken={setTurnstileToken} /> : <p className="microcopy">Loading Turnstile…</p>}
                <button className="primary-action" disabled={state.matches('submitting') || state.matches('archived')} onClick={onSubmit}>
                  {state.matches('archived') ? 'Saved to commons' : state.matches('submitting') ? 'Saving…' : 'Save anonymous curve'}
                </button>
                {saveNote ? <p className="microcopy save-note">{saveNote}</p> : null}
                {unsent && !state.matches('archived') ? <p className="microcopy">A previous unsent result exists in this browser.</p> : null}
              </section>
            </div>
          ) : null}
        </div>

        <aside className="side-stack">
          <section className="panel curve-panel" aria-labelledby="live-curve-title">
            <div className="panel-header">
              <div>
                <p className="eyebrow">live curve</p>
                <h2 id="live-curve-title">What 100 later-coins feel like today</h2>
              </div>
            </div>
            <CurveChart points={fingerprint.curve} commons={stats} />
            <p className="microcopy">Cyan is you. Dotted lavender is the anonymous commons mean when available.</p>
          </section>
          <div id="commons">
            <CommonsPanel stats={stats} />
            {commonsError ? <p className="microcopy error-text">{commonsError}</p> : null}
          </div>
        </aside>
      </section>

      <section id="method" className="panel method-panel">
        <p className="eyebrow">method</p>
        <h2>Precise enough to be useful. Humble enough not to cosplay a lab.</h2>
        <div className="method-grid">
          <article>
            <strong>Phenomenon</strong>
            <p>Delay discounting / hyperbolic discounting: delayed rewards lose subjective value, usually fastest near the present.</p>
          </article>
          <article>
            <strong>Model</strong>
            <p>Mazur-style hyperbola: <code>V = A / (1 + kD)</code>. Your k is the median implied k across delay probes.</p>
          </article>
          <article>
            <strong>Capture</strong>
            <p>Saved rows: anonymous device hash, choices, reaction times, curve points, fingerprint metrics. Public API returns aggregates only.</p>
          </article>
          <article>
            <strong>Cloudflare spend</strong>
            <p>Pages + Pages Functions + D1 + Turnstile. No Vectorize, Queues, Browser Rendering, or paid-only storage.</p>
          </article>
        </div>
      </section>
    </main>
  );
}
